import type { FC } from "react";
import { useForm } from "@tanstack/react-form";

import { usePatchPatient } from "@/api/patients";
import Edit from "@/assets/icons/edit";
import { FieldInput, FieldRadio, FieldSelect } from "@/components/form";
import DialogCustom from "@/components/modals/dialog-custom";
import RiskModalFooter from "@/components/modals/profil-risk/risk-modal-footer";
import type { RiskUpdateModalProps } from "@/components/modals/profil-risk/risk-update-modal";
import { Separator } from "@/components/ui/separator";
import {
  patchDiabeticDefaultValues,
  patchDiabeticSchema,
} from "@/schemas/patient";
import { resetForm } from "@/utils/form";
import { diabeticOptions, stateOptions } from "@/utils/patient";

const DiabetModal: FC<RiskUpdateModalProps> = ({
  riskModalOpen,
  setRiskModalOpen,
  patient,
}) => {
  const { mutate } = usePatchPatient(patient.id);

  const { diabetState, diabetic, diabetComment, hba1c } = patient;

  const { Field, handleSubmit } = useForm({
    defaultValues: {
      diabetState,
      diabetic,
      diabetComment: diabetComment ?? patchDiabeticDefaultValues.diabetComment,
      hba1c: hba1c ?? patchDiabeticDefaultValues.hba1c,
    },
    onSubmit: async ({ value, formApi }) => {
      const values = patchDiabeticSchema.parse(value);
      mutate(value, {
        onSuccess: () => {
          setRiskModalOpen("none");
          resetForm(values, formApi);
        },
      });
    },
  });

  return (
    <DialogCustom
      title="Modifier les informations"
      startIcon={
        <div className="flex p-3 w-12 h-12 justify-center items-center border border-gray-200 rounded-[10px]">
          <Edit className="w-6 h-6" color="black" />
        </div>
      }
      description={
        <FieldSelect
          Field={Field}
          schema={patchDiabeticSchema}
          name="diabetState"
          options={stateOptions}
          validators={{
            onChange: patchDiabeticSchema.shape.diabetState,
          }}
          risk={"diabetic"}
        />
      }
      open={riskModalOpen !== "none"}
      onOpenChange={() => setRiskModalOpen("none")}
      className="flex flex-col gap-4"
    >
      <Separator />
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full px-6">
        <FieldRadio
          Field={Field}
          schema={patchDiabeticSchema}
          name="diabetic"
          label="Patient diabétique"
          options={diabeticOptions}
          orientation="horizontal"
          validators={{
            onChange: patchDiabeticSchema.shape.diabetic,
          }}
        />
        <FieldInput
          Field={Field}
          schema={patchDiabeticSchema}
          name="hba1c"
          placeholder="4.5"
          label="HbA1c"
          type="number"
          min={0}
          max={100}
          isPercentage={true}
          validators={{
            onChange: patchDiabeticSchema.shape.hba1c,
          }}
        />
        <FieldInput
          Field={Field}
          schema={patchDiabeticSchema}
          name="diabetComment"
          placeholder="Ajouter un commentaire"
          label="Commentaire"
          validators={{
            onChange: patchDiabeticSchema.shape.diabetComment,
          }}
        />
        <input type="submit" className="hidden" />
      </form>
      <Separator />
      <RiskModalFooter
        setRiskModalOpen={setRiskModalOpen}
        handleSubmit={handleSubmit}
      />
    </DialogCustom>
  );
};

export default DiabetModal;
